import { Injectable, inject } from "@angular/core";
import { Subject, Observable } from "rxjs";
import { Utilities } from "@tylertech/shared-module";
import
{
    ClassMetadata,
    IFormMetadata,
    ISplitValue,
    IViewMetadata,
    KeyValuePair,
    PropertyMetadata,
} from "@tylertech/tyler-api-types";
import { AugmentedModelObject, MetadataKey } from "../interfaces";
import { MetadataService } from "./metadata.service";
import { MetadataViewService } from "./metadataViewService";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";

export interface AutoCompleteOption
{
    label: string;
    value: MetadataKey;
}

export interface OptionSource
{
    Id: MetadataKey;

    SourceField: string;

    Option: KeyValuePair<MetadataKey, string>;

    IdProperty: string;

    Model: AugmentedModelObject;

    InFilter: boolean;
}

@Injectable({ providedIn: "root" })
export class MetadataInputSelectService
{
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataViewService = inject(MetadataViewService);

    private readonly log = getLoggerFor("MetadataInputSelectService");
    protected readonly options = new Map<string, Array<OptionSource>>();
    protected readonly merge = new Map<string, boolean>();

    @LogInvocation(LogLevel.DEBUG)
    AddOrUpdateOption(
        metadata: ClassMetadata,
        metadataModel: AugmentedModelObject,
        type: string,
        propertyMetadata: PropertyMetadata,
        value: string
    ): void
    {
        if (propertyMetadata.PopulateLookup)
        {
            //property being updated is a PopulateLookup
            const display = Utilities.IsNullOrUndefined(propertyMetadata.PopulateLookup.DisplayProperty)
                ? value.toString()
                : metadataModel[propertyMetadata.PopulateLookup.DisplayProperty].toString();
            const lookupType = propertyMetadata.PopulateLookup.Lookup;
            if (!this.options.has(lookupType))
            {
                this.options.set(lookupType, []);
            }

            const kvp: KeyValuePair<MetadataKey, string> = {
                Key: metadataModel[propertyMetadata.PopulateLookup.IdProperty],
                Value: display,
            };

            const existingOption = this.options.get(lookupType).find(x =>
            {
                return x.SourceField === `${type}.${propertyMetadata.Name}`
                    && x.Id === metadataModel[propertyMetadata.PopulateLookup.IdProperty];
            });

            if (existingOption)
            {
                existingOption.Option = kvp;
            }
            else
            {
                const option: OptionSource = {
                    Id: metadataModel[propertyMetadata.PopulateLookup.IdProperty],
                    SourceField: `${type}.${propertyMetadata.Name}`,
                    Option: kvp,
                    IdProperty: propertyMetadata.PopulateLookup.IdProperty,
                    Model: metadataModel,
                    InFilter: true,
                };
                this.options.get(lookupType).push(option);
            }

            this.merge.set(lookupType, propertyMetadata.PopulateLookup.MergeOptions);
            return;
        }

        //property being updated is in a model with a PopulateLookup, and the new values may be needed
        const populateProperties = metadata.Properties.filter(x => x.PopulateLookup);
        if (populateProperties.length === 0)
        {
            return;
        }

        populateProperties.forEach(populateProperty =>
        {
            const lookup = this.options.get(populateProperty.PopulateLookup.Lookup);
            if (!lookup)
            {
                return;
            }
            const option = lookup.find(x => x.Id === metadataModel[populateProperty.PopulateLookup.IdProperty]);
            if (!option)
            {
                return;
            }

            option.InFilter = true;
            if (populateProperty.PopulateLookup.DisplayProperty)
            {
                option.Option.Value = metadataModel[populateProperty.PopulateLookup.DisplayProperty].toString();
            }

            option.Model = metadataModel;
        });
    }

    @LogInvocation(LogLevel.DEBUG)
    ClearOptions(form: IFormMetadata, model: AugmentedModelObject): void
    {
        this.clearFromViews(form.Views, model);
    }

    @LogInvocation(LogLevel.DEBUG)
    protected clearFromViews(views: IViewMetadata[], model: AugmentedModelObject): void
    {
        views.forEach(view =>
        {
            if (this.$metadataViewService.IsCombinedView(view))
            {
                this.clearFromViews(view.Views, model);
                return;
            }

            if (!view.ViewMetaData?.Properties)
            {
                return;
            }

            const populateProperties = view.ViewMetaData.Properties.filter(x => x.PopulateLookup);
            if (!populateProperties.length)
            {
                return;
            }

            populateProperties.forEach(populateProperty =>
            {
                const cachedOptions = this.options.get(populateProperty.PopulateLookup.Lookup);
                if (!cachedOptions)
                {
                    return;
                }

                const valueToRemove = model[populateProperty.Name];
                const remove = cachedOptions.find(x => x.Option.Value === valueToRemove);

                cachedOptions.splice(cachedOptions.indexOf(remove), 1);
            });
        });
    }

    ClearAllOptions(): void
    {
        this.options.clear();
    }

    RemoveOption(metadata: ClassMetadata, entity: any): void
    {
        metadata.Properties.filter(x => !Utilities.IsNullOrUndefined(x.PopulateLookup))
            .forEach(propertyMetadata =>
            {
                const lookupType = propertyMetadata.PopulateLookup.Lookup;
                const cachedOptions = this.options.get(lookupType);
                const remove = cachedOptions?.filter(
                    x => x.Option.Value === entity[propertyMetadata.Name].toString());

                if (!Utilities.IsNullOrUndefinedOrEmpty(remove))
                {
                    cachedOptions.splice(cachedOptions.indexOf(remove[0]), 1);
                }
            });
    }

    MergeSelectOptions(
        lookupType: string,
        results: KeyValuePair<MetadataKey, string>[],
        propertyMetadata: PropertyMetadata,
        metadataModel?: AugmentedModelObject
    ): KeyValuePair<MetadataKey, string>[]
    {
        if (Utilities.IsNullOrUndefined(this.options.get(lookupType)))
        {
            return results;
        }

        const options = this.getPopulatedOptions(lookupType, propertyMetadata, metadataModel);
        return this.mergeOptions(options, results, lookupType);
    }

    MergeTypeAheadOptions(
        lookupType: string,
        results: AutoCompleteOption[],
        propertyMetadata: PropertyMetadata,
        metadataModel: AugmentedModelObject,
        parent: AugmentedModelObject,
        searchText?: string
    ): AutoCompleteOption[]
    {
        if (Utilities.IsNullOrUndefined(this.options.get(lookupType)))
        {
            return results;
        }
        let populatedOptions = this.getPopulatedOptions(lookupType, propertyMetadata, metadataModel);

        if (!Utilities.IsNullOrUndefinedOrEmpty(searchText))
        {
            populatedOptions = populatedOptions.filter(f => f.Value.toLowerCase().includes(searchText.toLowerCase()));
        }

        const options: Array<AutoCompleteOption> = populatedOptions
            .map(m =>
            {
                return {
                    value: m.Key,
                    label: m.Value
                };
            });

        if (this.merge.get(lookupType))
        {
            options.forEach(option =>
            {
                const existing = results.find(f => String(f.value) === String(option.value));
                if (existing)
                {
                    existing.label = option.label;
                }
                else
                {
                    results.push(option);
                }
            });

            return results;
        }

        return options;

    }

    @LogInvocation(LogLevel.DEBUG)
    protected getPopulatedOptions(
        lookupType: string,
        propertyMetadata: PropertyMetadata,
        metadataModel?: AugmentedModelObject,
        parent?: AugmentedModelObject
    ): KeyValuePair<MetadataKey, string>[]
    {
        const lookupTypeOptions = this.options.get(lookupType);
        lookupTypeOptions.forEach(f =>
        {
            f.InFilter = true;
        });

        if (!Utilities.IsNullOrUndefined(propertyMetadata.FilterBy))
        {
            const filterByFields = propertyMetadata.FilterBy.split(",")
                .filter(x => !!x);
            for (const depString of filterByFields)
            {
                let field = depString.split("|")
                    .pop();
                field = field.split(".")
                    .pop();

                const value = this.$metadataService.DependentModuleValue(depString, parent, metadataModel);
                for (let i = 0; i < lookupTypeOptions.length; i++)
                {
                    const source: OptionSource = lookupTypeOptions[i];
                    //only check filter if the entity that the added option is from is the same type of the entity that this lookup is on
                    if (source.Model.$$TypeName === metadataModel.$$TypeName)
                    {
                        if (value === undefined)
                        {
                            //undefined is a valid value at this point for comparison
                            if (source.Model[field] !== undefined)
                            {
                                source.InFilter = false;
                            }
                        }
                        else
                        {
                            if (source.Model[field].toString() !== value.toString())
                            {
                                source.InFilter = false;
                            }
                        }
                    }
                }
            }
        }

        let options = lookupTypeOptions.filter(f => f.InFilter);
        if (!Utilities.IsNullOrUndefined(metadataModel) && options.length > 0)
        {
            //If the model of the field populating the lookup is the same type and Id as the model of the target lookup (sibling properties), exclude this value from the options
            options = options.filter(f =>
            {
                return f.Model.$$TypeName !== metadataModel.$$TypeName
                    || f.Id !== metadataModel[f.IdProperty];
            });
        }
        return options.map(x => x.Option);
    }

    @LogInvocation(LogLevel.DEBUG)
    protected mergeOptions(
        options: KeyValuePair<MetadataKey, string | ISplitValue>[],
        results: KeyValuePair<MetadataKey, string | ISplitValue>[],
        lookupType: string
    ): KeyValuePair<MetadataKey, string | any>[]
    {
        if (Utilities.IsNullOrUndefined(results))
            results = [];

        if (this.merge.get(lookupType))
        {
            options.forEach(option =>
            {
                const existing = results.find(f => String(f.Key) === String(option.Key));
                if (existing)
                {
                    existing.Value = option.Value;
                }
                else
                {
                    results.push(option);
                }
            });
            return results;
        }
        return options;
    }

    private readonly multiSelectChange$ = new Subject<string>();

    get onMultiSelectChange(): Observable<string>
    {
        return this.multiSelectChange$.asObservable();
    }

    fireMultiSelectChangeListener(name: string): void
    {
        this.multiSelectChange$.next(name);
    }

    private readonly multiSelectScroll$ = new Subject<{ name: string; event: Event }>();

    get onMultiSelectScroll(): Observable<{ name: string; event: Event }>
    {
        return this.multiSelectScroll$.asObservable();
    }

    fireMultiSelectScrollListener(name: string, event: Event): void
    {
        this.multiSelectScroll$.next({ name, event });
    }

    cleanup(): void
    {
        this.merge.clear();
        this.ClearAllOptions();
    }
}
